import { useEffect, useState } from 'react';
import { Check, Copy, Eye, EyeOff, KeyRound, Plus, Trash2 } from 'lucide-react';
import { createApiToken, deleteApiToken, fetchApiTokens, revealApiToken } from '../lib/api';
import config from '../config';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Spinner from '../components/Spinner';
import { cn } from '../lib/utils';

// Where integrations should point. In production the API is served on the
// app's own origin (VITE_API_URL empty → '/'); locally it's the Vite env value.
const apiBase = () => (
  config.apiUrl === '/' || !config.apiUrl
    ? window.location.origin
    : config.apiUrl.replace(/\/$/, '')
);

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'never');

function CopyButton({ text, label = 'copy' }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <button type="button" onClick={copy} className="flex items-center gap-1 text-xs text-primary hover:underline">
      {done ? <><Check className="h-3 w-3" /> copied</> : <><Copy className="h-3 w-3" /> {label}</>}
    </button>
  );
}

function Snippet({ title, children, code, note }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-mono text-sm text-muted-foreground">{'// '}{title}</h3>
        <CopyButton text={code} />
      </div>
      {children ? <p className="mt-1 text-xs text-muted-foreground">{children}</p> : null}
      <pre className="mt-2 overflow-x-auto rounded-md bg-muted/60 p-3 font-mono text-xs leading-relaxed">{code}</pre>
      {note ? <p className="mt-2 text-xs text-muted-foreground">{note}</p> : null}
    </div>
  );
}

const TOOLS = [
  ['create_ticket', 'file a ticket — people by name, "me" for yourself, due dates, repeats'],
  ['list_tickets', 'what is open, mine, unassigned, or matching a search'],
  ['get_ticket', 'details + latest messages of #42'],
  ['update_ticket', 'move status (done…), reprioritise, reassign, set a due date'],
  ['add_comment', 'post on a ticket thread, @mention to notify'],
  ['list_people', 'who exists and who can be assigned'],
];

export default function IntegrationsPage() {
  const [tokens, setTokens] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The token whose key is currently shown (just minted, or revealed from the
  // list): { id, name, key, isNew }. Kept in memory only; it also feeds the
  // snippets below so they can be copied ready to use.
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchApiTokens().then(setTokens).catch((e) => {
      setTokens([]);
      setError(e?.response?.data?.detail || 'Could not load your tokens.');
    });
  }, []);

  const mint = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const t = await createApiToken(name.trim());
      setActive({ id: t.id, name: t.name, key: t.key, isNew: true });
      setTokens((prev) => [{ ...t, key: undefined }, ...(prev || [])]);
      setName('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create the token.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke "${t.name}"? Anything using it stops working immediately.`)) return;
    try {
      await deleteApiToken(t.id);
      setTokens((prev) => prev.filter((x) => x.id !== t.id));
      if (active?.id === t.id) setActive(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not revoke the token.');
    }
  };

  const reveal = async (t) => {
    if (active?.id === t.id) { setActive(null); return; }
    setError('');
    try {
      const r = await revealApiToken(t.id);
      setActive({ id: r.id, name: r.name, key: r.key, isNew: false });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not reveal the token.');
    }
  };

  const base = apiBase();
  const key = active?.key || '<YOUR_TOKEN>';
  const mcpUrl = `${base}/api/mcp/`;
  const mcpTokenUrl = `${base}/api/mcp/${key}/`;

  const snippets = {
    claude: mcpTokenUrl,
    code: `claude mcp add --transport http ticky ${mcpUrl} --header "Authorization: Bearer ${key}"`,
    json: JSON.stringify({ mcpServers: { ticky: { url: mcpUrl, headers: { Authorization: `Bearer ${key}` } } } }, null, 2),
    curl: [
      `curl -X POST ${base}/api/integrations/tickets/ \\`,
      `  -H "Authorization: Bearer ${key}" \\`,
      '  -H "Content-Type: application/json" \\',
      '  -d \'{"title": "Stock sync fails for 3 SKUs", "type": "bug", "priority": "high",',
      '       "assignees": ["robbie"], "due_date": "2026-09-15", "description": "…"}\'',
    ].join('\n'),
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="font-mono text-lg font-semibold">{'// integrations'}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          File and read tickets from Claude, Claude Code, Cursor, Zapier, Shortcuts or a script — as you.
          Generate a personal token, then paste one of the snippets below. Tokens have your permissions, so
          treat them like a password.
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
      ) : null}

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="font-mono text-sm text-muted-foreground">{'// your tokens'}</h2>
        <form onSubmit={mint} className="mt-3 flex flex-wrap items-center gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Name it after where it lives — "Claude Desktop", "Cursor laptop"'
            className="min-w-[240px] flex-1"
            maxLength={80}
          />
          <Button type="submit" disabled={busy || !name.trim()}>
            <Plus className="mr-1.5 h-4 w-4" /> {busy ? 'Creating…' : 'New token'}
          </Button>
        </form>

        {active ? (
          <div className="mt-3 rounded-md border border-primary/40 bg-primary/5 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <KeyRound className="h-4 w-4 text-primary" /> {active.name}
                {active.isNew ? <span className="rounded bg-primary/15 px-1.5 text-[10px] font-semibold uppercase text-primary">new</span> : null}
              </p>
              <CopyButton text={active.key} label="copy token" />
            </div>
            <code className="mt-2 block break-all rounded bg-muted/60 px-2 py-1.5 font-mono text-xs">{active.key}</code>
            <p className="mt-2 text-xs text-muted-foreground">
              Keep it private — anyone holding it can act as you in Ticky. The snippets below include it,
              and you can reveal it again here whenever you set up another device.
            </p>
          </div>
        ) : null}

        <div className="mt-3">
          {tokens === null ? <Spinner /> : tokens.length === 0 ? (
            <p className="text-xs text-muted-foreground">No tokens yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1.5 font-medium">name</th>
                  <th className="py-1.5 font-medium">token</th>
                  <th className="py-1.5 font-medium">created</th>
                  <th className="py-1.5 font-medium">last used</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} className={cn('border-t border-border', active?.id === t.id && 'bg-primary/5')}>
                    <td className="py-2 pr-3 font-medium">{t.name}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-2">
                        <span title="Only the start is listed — use reveal to see the whole token">{t.hint}••••••••</span>
                        <button
                          type="button"
                          onClick={() => reveal(t)}
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-sans text-primary transition hover:bg-primary/10"
                          title={active?.id === t.id ? 'Hide the token' : 'Show the whole token and use it in the snippets'}
                        >
                          {active?.id === t.id ? <><EyeOff className="h-3 w-3" /> hide</> : <><Eye className="h-3 w-3" /> reveal</>}
                        </button>
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{fmt(t.created_at)}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{fmt(t.last_used_at)}</td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => revoke(t)}
                        className="rounded px-1.5 py-0.5 text-xs text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
                        title="Revoke this token"
                      >
                        <Trash2 className="inline h-3.5 w-3.5" /> revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Snippet
          title="claude.ai / Claude Desktop"
          code={snippets.claude}
          note="Then say: “Create a Ticky ticket for Robbie: stock sync fails for 3 SKUs, high priority, due Friday.”"
        >
          Settings → Connectors → Add custom connector → paste this URL as the server URL (no OAuth needed —
          the token is in the URL). Works with voice dictation (Wispr Flow etc.) since you just talk to Claude.
        </Snippet>
        <Snippet title="Claude Code" code={snippets.code}>
          Run once in your terminal; add <span className="font-mono">--scope user</span> to have it in every project.
        </Snippet>
        <Snippet title="Cursor / Windsurf / other MCP clients" code={snippets.json}>
          Add to the client&apos;s MCP config (e.g. <span className="font-mono">~/.cursor/mcp.json</span>).
        </Snippet>
        <Snippet title="REST — curl, Zapier, Shortcuts, n8n" code={snippets.curl}>
          People by name/username/email, values by label (&quot;bug&quot;, &quot;high&quot;, &quot;every week&quot;).
          You are the requestor unless you pass one. <span className="font-mono">GET</span> the same URL for
          your open tickets. The regular <span className="font-mono">/api/…</span> endpoints accept the token too.
        </Snippet>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="font-mono text-sm text-muted-foreground">{'// what Claude can do once connected'}</h2>
        <ul className="mt-2 grid gap-1.5 text-sm sm:grid-cols-2 xl:grid-cols-3">
          {TOOLS.map(([tool, what]) => (
            <li key={tool} className="flex gap-2">
              <code className="shrink-0 rounded bg-muted/60 px-1.5 font-mono text-xs leading-6">{tool}</code>
              <span className="text-muted-foreground">{what}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
